import React, { useState, useCallback } from 'react';
import { AlertTriangle, Lock } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

/**
 * ConfirmDialog — controlled wrapper around Shadcn AlertDialog.
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title, description, impactSummary, confirmLabel, tone: 'destructive' });
 *   if (ok) doIt();
 */

const ConfirmDialogContext = React.createContext(null);

export const ConfirmDialogProvider = ({ children }) => {
  const [state, setState] = useState(null); // { open, title, description, ..., resolve }

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setState({ open: true, ...opts, resolve });
    });
  }, []);

  const close = (ok) => {
    if (state?.resolve) state.resolve(ok);
    setState((s) => (s ? { ...s, open: false } : null));
    // unmount after animation
    setTimeout(() => setState(null), 200);
  };

  return (
    <ConfirmDialogContext.Provider value={confirm}>
      {children}
      {state && (
        <AlertDialog open={state.open} onOpenChange={(o) => !o && close(false)}>
          <AlertDialogContent data-testid="confirm-dialog">
            <AlertDialogHeader>
              <div className="flex items-center gap-2.5">
                <div className={cn(
                  'h-9 w-9 rounded-full flex items-center justify-center shrink-0',
                  state.tone === 'destructive' ? 'bg-destructive/15 text-destructive' : 'bg-warning/15 text-warning'
                )}>
                  {state.tone === 'destructive' ? <Lock className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                </div>
                <AlertDialogTitle className="text-base">{state.title}</AlertDialogTitle>
              </div>
              {state.description && (
                <AlertDialogDescription className="text-sm leading-relaxed pl-12">
                  {state.description}
                </AlertDialogDescription>
              )}
            </AlertDialogHeader>

            {state.impactSummary && state.impactSummary.length > 0 && (
              <div className="ml-12 rounded-lg border border-border bg-muted/40 p-3 space-y-1">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Impact preview</p>
                {state.impactSummary.map((row, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{row.label}</span>
                    <span className="font-mono-num font-medium text-foreground">{row.value}</span>
                  </div>
                ))}
              </div>
            )}

            <AlertDialogFooter>
              <AlertDialogCancel data-testid="confirm-cancel">{state.cancelLabel || 'Cancel'}</AlertDialogCancel>
              <AlertDialogAction
                data-testid="confirm-ok"
                onClick={() => close(true)}
                className={cn(
                  state.tone === 'destructive'
                    ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90'
                )}
              >
                {state.confirmLabel || 'Confirm'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </ConfirmDialogContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = React.useContext(ConfirmDialogContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmDialogProvider');
  return ctx;
};
